// S6.5 Phase 3 — AskUserQuestionCard.
//
// Renders a Claude AskUserQuestion tool call as a multi-question chip
// cluster. Each question is its own row; options render as touch-friendly
// buttons. Multi-select questions add a Submit button.
//
// Wire contract (per addendum §A2-E1 ERRATA, validated in Phase 0.10):
// the dashboard returns `{ decision: 'allow', answers: { [questionText]:
// chosenLabel } }` to the resolver endpoint. The wrapper translates this
// into `hookSpecificOutput.updatedInput.answers` which the CLI synthesizes
// into a positive tool_result. The ORIGINAL questions[] array round-trips
// untouched.

import { useMemo, useState } from 'react';
import { Icon } from '../atoms/Icon';
import { useTranslation } from '../../i18n';
import {
  type AskUserQuestionPendingContent,
  type AskUserQuestionEntry,
  type AskUserQuestionOption,
  type PermissionTerminalContent,
  optionLabel,
  optionDescription,
} from '../../lib/permissions/types';
import { resolvePermission } from '../../lib/api';
import { useToastStore } from '../../stores/toast.store';
import { PermissionOutcomePill } from './PermissionOutcomePill';

export interface AskUserQuestionCardProps {
  agentId: string;
  pending: AskUserQuestionPendingContent;
  terminal?: PermissionTerminalContent;
  sticky?: boolean;
}

export function AskUserQuestionCard({
  agentId,
  pending,
  terminal,
  sticky,
}: AskUserQuestionCardProps) {
  if (terminal) {
    // Reuse the same outcome pill; tool_input is the questions[] array.
    return (
      <PermissionOutcomePill
        toolName={pending.tool_name}
        toolInput={{ questions: pending.questions }}
        terminal={terminal}
      />
    );
  }
  return (
    <PendingAskCard
      agentId={agentId}
      pending={pending}
      sticky={sticky ?? false}
    />
  );
}

function PendingAskCard({
  agentId,
  pending,
  sticky,
}: {
  agentId: string;
  pending: AskUserQuestionPendingContent;
  sticky: boolean;
}) {
  const { t } = useTranslation();
  const pushToast = useToastStore((s) => s.push);
  const [submitting, setSubmitting] = useState(false);
  // selections keyed by questionText. For single-select: stores the chosen
  // label. For multi-select: stores an array of chosen labels.
  const [selections, setSelections] = useState<Record<string, string | string[]>>({});

  const questions = pending.questions ?? [];

  const allAnswered = useMemo(() => {
    return questions.every((q) => {
      const v = selections[q.question];
      if (q.multiSelect) return Array.isArray(v) && v.length > 0;
      return typeof v === 'string' && v.length > 0;
    });
  }, [questions, selections]);

  const handlePick = (q: AskUserQuestionEntry, label: string) => {
    if (submitting) return;
    setSelections((prev) => {
      if (q.multiSelect) {
        const arr = Array.isArray(prev[q.question]) ? (prev[q.question] as string[]) : [];
        const next = arr.includes(label) ? arr.filter((x) => x !== label) : [...arr, label];
        return { ...prev, [q.question]: next };
      }
      return { ...prev, [q.question]: label };
    });
    // Single-select with one question: auto-submit on click. Multi-select
    // and multi-question both wait for the explicit Submit button.
    if (!q.multiSelect && questions.length === 1) {
      void submit({ [q.question]: label });
    }
  };

  const submit = async (override?: Record<string, string | string[]>) => {
    if (submitting) return;
    const answers = override ?? selections;
    setSubmitting(true);
    try {
      await resolvePermission(agentId, pending.request_id, {
        decision: 'allow',
        answers,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('askQuestion.couldNotSend');
      pushToast({
        message: msg.includes('REQUEST_GONE')
          ? t('askQuestion.alreadyAnswered')
          : t('askQuestion.couldNotSendDetail', { error: msg }),
      });
      setSubmitting(false);
    }
  };

  return (
    <div
      className={
        (sticky ? 'sticky top-0 z-card-sticky ' : '') +
        'rounded-md border border-border/50 border-l-risk-stripe border-l-status-info bg-popover/95 backdrop-blur-md p-4 shadow-sm'
      }
      role="region"
      aria-label={t('askQuestion.askingQuestion')}
      data-testid="ask-user-question-card"
      data-request-id={pending.request_id}
    >
      <div className="flex items-start gap-3">
        <Icon
          name="HelpCircle"
          className="shrink-0 mt-0.5 text-status-info"
          size={18}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="text-caption md:text-body font-semibold text-foreground leading-snug">
            {t('askQuestion.heading')}
          </div>
          <div className="mt-1 text-caption text-muted-foreground/45">
            {questions.length === 1
              ? t('askQuestion.pickOption')
              : t('askQuestion.answerQuestions', { count: questions.length })}
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {questions.map((q, qi) => (
          <QuestionRow
            key={`${q.question}-${qi}`}
            question={q}
            selection={selections[q.question]}
            onPick={(label) => handlePick(q, label)}
            disabled={submitting}
          />
        ))}
      </div>

      {(questions.length > 1 || questions.some((q) => q.multiSelect)) && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            disabled={!allAnswered || submitting}
            onClick={() => void submit()}
            className="min-h-touch-target px-4 rounded-md bg-action hover:bg-action/90 disabled:opacity-40 text-body font-medium text-action-foreground transition-colors flex items-center gap-2"
            data-testid="ask-user-question-submit"
          >
            {submitting && <Icon name="Loader2" size={14} className="animate-spin" aria-hidden="true" />}
            {t('askQuestion.submit')}
          </button>
        </div>
      )}
    </div>
  );
}

function QuestionRow({
  question,
  selection,
  onPick,
  disabled,
}: {
  question: AskUserQuestionEntry;
  selection: string | string[] | undefined;
  onPick: (label: string) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const options: AskUserQuestionOption[] = question.options ?? [];
  return (
    <div data-testid="ask-user-question-row">
      {question.header && (
        <div className="text-caption text-muted-foreground/70 mb-1 break-words">
          {question.header}
        </div>
      )}
      <div className="text-body text-foreground break-words mb-2">
        {question.question}
        {question.multiSelect && (
          <span className="ml-2 text-caption text-muted-foreground/45">{t('askQuestion.pickAny')}</span>
        )}
      </div>
      <div className="flex flex-col gap-2">
        {options.map((opt, oi) => {
          const label = optionLabel(opt);
          const description = optionDescription(opt);
          const active = question.multiSelect
            ? Array.isArray(selection) && selection.includes(label)
            : selection === label;
          return (
            <button
              key={`${label}-${oi}`}
              type="button"
              disabled={disabled}
              onClick={() => onPick(label)}
              aria-pressed={question.multiSelect ? active : undefined}
              className={
                'min-h-touch-target px-4 py-2 rounded-md text-left text-body font-medium transition-colors disabled:opacity-60 ' +
                (active
                  ? 'bg-action text-action-foreground border border-action hover:bg-action/90'
                  : 'bg-card text-foreground border border-border/50 hover:bg-accent')
              }
              data-testid="ask-user-question-option"
            >
              <div className="break-words">{label}</div>
              {description && (
                <div
                  className={
                    'mt-1 text-caption font-normal break-words ' +
                    (active ? 'text-action-foreground/80' : 'text-muted-foreground/70')
                  }
                  data-testid="ask-user-question-option-description"
                >
                  {description}
                </div>
              )}
            </button>
          );
        })}
        {options.length === 0 && (
          <div className="text-caption text-muted-foreground/45 italic">{t('askQuestion.noOptions')}</div>
        )}
      </div>
    </div>
  );
}
